import { Button, Col, Input, Modal, Row } from "antd";
import { debounce } from "lodash";
import moment from "moment-timezone";
import { useEffect, useMemo, useRef, useState } from "react";
import { IoSearchOutline } from "react-icons/io5";
import apiFactory from "../../../api";
import { CHATTING } from "../../../config/Constant";
import { useAuthLayoutContext } from "../../../context/AuthLayoutContext";
import { useInfoUser } from "../../../store/UserStore";
import { updateOrgList } from "../../../utils/OrganizationHelper";
import { openNotificationError } from "../../../utils/Utils";
import Checkbox from "antd/es/checkbox/Checkbox";
import { CustomAvatar } from "../../avatar/CustomAvatar";

const REACT_APP_CALL_NUMBER = process.env.REACT_APP_CALL_NUMBER || 6;

const BasicMemberInfo = ({ member, onChangeMembersCheckBox }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 860);

  const handleResize = () => {
    setIsMobile(window.innerWidth < 860);
  };

  const getPaths = () => {
    return member?.paths?.map((path) => path?.name)?.join(" > ");
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <Row
      gutter={isMobile ? [4, 0] : [8, 0]}
      className="w-full mb-5 items-center"
    >
      <Col span={isMobile ? 5 : 3} className="flex items-center justify-center">
        <CustomAvatar
          person={member}
          userId={member?.userCode}
          pos={-1}
          styleCss={{}}
          isShowUserStatus={{ status: true, isAlwaysTrue: false }}
        />
      </Col>
      <Col span={isMobile ? 15 : 18} className="items-center justify-center">
        <Row
          gutter={isMobile ? [4, 0] : [8, 0]}
          className="items-center justify-center text-sm"
        >
          <Col span={isMobile ? 24 : 10}>
            <span className="font-bold text-[14px]">{member?.name}</span>
          </Col>

          <Col span={isMobile ? 24 : 14}>{getPaths()}</Col>
        </Row>
      </Col>
      <Col
        span={3}
        className="flex flex-1 items-center justify-end text-[10px]"
      >
        <Checkbox
          checked={member?.checked}
          onChange={(e) => {
            onChangeMembersCheckBox(member?.userCode);
          }}
        />
      </Col>
    </Row>
  );
};

const PickMemberModal = ({
  selectedConversation,
  isPickCallMember,
  setIsPickCallMember,
  memberIds,
  roomId,
}) => {
  /* Context */
  const {
    setOrgList: setOriginalOrgList,
    callingType,
    openCall,
    getMember,
  } = useAuthLayoutContext();

  const { user, languageMap } = useInfoUser();
  const [memberList, setMemberList] = useState([]);
  const [memberPage, setMemberPage] = useState(1);
  const [memberNameSearch, setMemberNameSearch] = useState("");
  const [isMoreMember, setIsMoreMember] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const lastObserver = useRef();

  const addMember = async () => {
    await apiFactory.callingApi.addMember({
      roomId,
      memberIds: selectedUserIds,
    });
  };

  const onCall = async () => {
    if (roomId) {
      await addMember();
    } else {
      openCall(
        [...selectedUserIds, user?.userId],
        callingType,
        selectedConversation?.conversationId,
      );

    }

    setIsPickCallMember(false);
  };

  const lastMemberRef = (node) => {
    if (isLoading || !isMoreMember) return;
    if (lastObserver.current) lastObserver.current.disconnect();

    lastObserver.current = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting) {
        setIsLoading(true);
        setMemberPage(memberPage + 1);
      }
    });

    if (node) lastObserver.current.observe(node);
  };

  const fetchMembers = async () => {
    setIsLoading(true);

    const result =
      await apiFactory?.userConversationApi?.getListUserConversation({
        conversationId: selectedConversation?.conversationId,
        limit: CHATTING.MESSAGE_LIMIT,
        page: memberPage,
        search: {
          memberName: memberNameSearch,
          viewType: "ALL",
        },
        ts: moment(),
      });

    if (result?.status !== 200) {
      openNotificationError(result?.message);
      setIsLoading(false);
      return;
    }

    if (result?.data) {
      updateOrgList(setOriginalOrgList, result?.data?.items);

      const members = result?.data?.items
        ?.filter((m) => !memberIds?.includes(m?.userId))
        ?.map((m) => {
          return getMember(m?.userId);
        });

      if (result?.data?.items?.length < CHATTING?.MESSAGE_LIMIT)
        setIsMoreMember(false);

      if (memberPage === 1) {
        setMemberList([...members]);
      } else {
        setMemberList((prev) => [...prev, ...members]);
      }
    }

    setIsLoading(false);
  };

  const debouncedSearchMember = debounce((memberName) => {
    setMemberNameSearch(memberName);
    setMemberPage(1);
  }, 500);

  const selectedUserIds = useMemo(() => {
    return memberList
      ?.filter((member) => member?.checked)
      ?.map((member) => member?.userCode);
  }, [memberList]);

  const onChangeMembersCheckBox = (userId) => {
    setMemberList((prev) => {
      const index = prev?.findIndex((member) => member?.userCode === userId);

      prev[index].checked = !prev?.[index].checked;

      return [...prev];
    });
  };

  useEffect(() => {
    fetchMembers();
  }, [memberPage, memberNameSearch]);

  return (
    <Modal
      title={
        // languageMap?.["modal.memberList.title"] ??
        "Member List" +
        (selectedUserIds?.length + memberIds?.length > REACT_APP_CALL_NUMBER
          ? ` exceeded ${REACT_APP_CALL_NUMBER} persons`
          : "")
      }
      open={isPickCallMember}
      onCancel={() => setIsPickCallMember(false)}
      width="800px"
      // centered
      footer={false}
    >
      <div className="mt-4 flex flex-col justify-between">
        <div className=" modal-member-mobile ">
          <div className="mt-2 mb-2">
            <Input
              placeholder={
                languageMap?.["menu.searchMember.placeholder"] ?? "Search"
              }
              prefix={<IoSearchOutline size={15} />}
              onChange={(e) => debouncedSearchMember(e?.target?.value)}
            />
          </div>
          <div className="mt-5 modal-show-member-container overflow-y-scroll h-[50vh]">
            {memberList?.map((member, index) => (
              <div
                key={member?.userId}
                ref={index === memberList?.length - 1 ? lastMemberRef : null}
              >
                <BasicMemberInfo
                  member={member}
                  onChangeMembersCheckBox={onChangeMembersCheckBox}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <div className="h-[30px] flex items-center gap-2">
            {selectedUserIds?.length > 0 && (
              <Button
                className="w-[90px] bg-[#2a56b9] text-white"
                onClick={onCall}
                disabled={
                  selectedUserIds?.length + memberIds?.length >
                  REACT_APP_CALL_NUMBER
                }
              >
                Call
              </Button>
            )}
            <Button
              className="w-[90px] bg-[#d9d9d9]"
              onClick={() => setIsPickCallMember(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
export { PickMemberModal };
